import {
  LoadVelocityFit,
  LoadVelocityPoint,
  LoadVelocitySet,
} from './EstimatedOneRepMaxCard.types';

export const POUNDS_PER_KILOGRAM = 2.2046226218;

// Keep only the sets that parsed into a usable load/velocity pair.
export const toLoadVelocityPoints = (sets: LoadVelocitySet[]): LoadVelocityPoint[] =>
  sets.reduce<LoadVelocityPoint[]>((points, set) => {
    const load = parseFloat(set.load);
    const velocity = parseFloat(set.velocity);

    if (isFinite(load) && isFinite(velocity) && load > 0 && velocity > 0) {
      points.push({ load, velocity });
    }

    return points;
  }, []);

// Ordinary least squares regression of velocity on load.
export const fitLoadVelocity = (points: LoadVelocityPoint[]): LoadVelocityFit | null => {
  const count = points.length;
  if (count < 2) return null;

  let sumLoad = 0;
  let sumVelocity = 0;
  let sumProduct = 0;
  let sumLoadSquared = 0;

  points.forEach(({ load, velocity }) => {
    sumLoad += load;
    sumVelocity += velocity;
    sumProduct += load * velocity;
    sumLoadSquared += load * load;
  });

  const denominator = count * sumLoadSquared - sumLoad * sumLoad;
  if (denominator === 0) return null;

  const slope = (count * sumProduct - sumLoad * sumVelocity) / denominator;

  return { slope, intercept: (sumVelocity - slope * sumLoad) / count };
};

/**
 * Read the load off the fitted line at the velocity at 1RM. Velocity has to fall as load
 * rises for the profile to be valid, so an upward slope returns no estimate.
 */
export const estimateOneRepMax = (
  sets: LoadVelocitySet[],
  velocityAtOneRepMax: number
): number | null => {
  const points = toLoadVelocityPoints(sets);
  if (points.length < 2 || !isFinite(velocityAtOneRepMax) || velocityAtOneRepMax <= 0) return null;

  const fit = fitLoadVelocity(points);
  if (!fit || fit.slope >= 0) return null;

  const oneRepMax = (velocityAtOneRepMax - fit.intercept) / fit.slope;

  return isFinite(oneRepMax) && oneRepMax > 0 ? oneRepMax : null;
};

export const convertLoad = (load: string, factor: number): string => {
  const value = parseFloat(load);
  if (!isFinite(value)) return load;

  return String(Math.round(value * factor * 10) / 10);
};
